<script setup>
import { computed, ref } from 'vue';
import {
  addDays,
  addHours,
  addMinutes,
  format,
  nextSaturday,
  setHours,
  setMinutes,
  setSeconds,
} from 'date-fns';
import { useI18n } from 'vue-i18n';
import SnoozeDateTimePickerMenu from './SnoozeDateTimePickerMenu.vue';

const props = defineProps({
  currentDate: {
    type: Date,
    default: () => new Date(),
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();
const showDateTimePicker = ref(false);

const setTime = (date, hours) =>
  setSeconds(setMinutes(setHours(date, hours), 0), 0);

const laterTodayDate = currentDate => {
  const todayAtSix = setTime(currentDate, 18);
  if (todayAtSix > currentDate) {
    return todayAtSix;
  }

  return setTime(addDays(currentDate, 1), 8);
};

const quickActions = computed(() => {
  const currentDate = props.currentDate;

  return [
    {
      id: 'thirty_minutes_later',
      label: t(
        'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.THIRTY_MINUTES_LATER'
      ),
      value: addMinutes(currentDate, 30),
    },
    {
      id: 'one_hour_later',
      label: t(
        'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.ONE_HOUR_LATER'
      ),
      value: addHours(currentDate, 1),
    },
    {
      id: 'three_hours_later',
      label: t(
        'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.THREE_HOURS_LATER'
      ),
      value: addHours(currentDate, 3),
    },
    {
      id: 'later_today',
      label: t(
        'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.LATER_TODAY'
      ),
      value: laterTodayDate(currentDate),
    },
    {
      id: 'tomorrow',
      label: t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.TOMORROW'),
      value: setTime(addDays(currentDate, 1), 8),
    },
    {
      id: 'later_this_week',
      label: t(
        'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.LATER_THIS_WEEK'
      ),
      value: setTime(addDays(currentDate, 2), 8),
    },
    {
      id: 'this_weekend',
      label: t(
        'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.THIS_WEEKEND'
      ),
      value: setTime(nextSaturday(currentDate), 8),
    },
    {
      id: 'next_week',
      label: t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.NEXT_WEEK'),
      value: setTime(addDays(currentDate, 7), 8),
    },
  ];
});

const formattedDate = date => format(date, 'EEE, h:mm a');

const selectQuickAction = value => {
  emit('select', { type: 'preset', value });
};

const selectCustomDateTime = () => {
  showDateTimePicker.value = true;
};

const hideDateTimePicker = () => {
  showDateTimePicker.value = false;
};

const applyCustomDateTime = value => {
  emit('select', { type: 'customDateTime', value });
};
</script>

<template>
  <SnoozeDateTimePickerMenu
    v-if="showDateTimePicker"
    @cancel="hideDateTimePicker"
    @apply="applyCustomDateTime"
  />
  <div v-else class="min-w-[280px]">
    <p class="mb-0 px-3 py-2 text-sm font-medium text-n-slate-11">
      {{ $t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.TITLE') }}
    </p>

    <div
      v-for="action in quickActions"
      :key="action.id"
      class="flex items-center justify-between gap-4 rounded-xl px-3 py-2.5 text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2"
      role="button"
      @click.stop="selectQuickAction(action.value)"
    >
      <span class="truncate">{{ action.label }}</span>
      <span class="shrink-0 text-sm text-n-slate-10">
        {{ formattedDate(action.value) }}
      </span>
    </div>

    <div class="mx-3 my-1 h-px bg-n-weak" />

    <div
      class="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2"
      role="button"
      @click.stop="selectCustomDateTime"
    >
      <span class="i-lucide-calendar-days size-4 text-n-slate-11" />
      <span>{{
        $t(
          'CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.QUICK_ACTIONS.SELECT_DATE_TIME'
        )
      }}</span>
    </div>
  </div>
</template>
